import { computed, effect, signal } from "@preact/signals-core";
import { bindClass, bindShow, html } from "../lib/bind";
import { type IconName, icon } from "../lib/icons";
import { brandLockup } from "../lib/logo";
import { effectScope } from "../lib/scope";
import { logout } from "../services/auth";
import { listCompanies } from "../services/company";
import { breadcrumb } from "../stores/breadcrumb";
import { currentPath } from "../stores/route";
import { session } from "../stores/session";

interface NavItem {
  path: string;
  label: string;
  icon: IconName;
  /** Sibling routes that should also count as this item being active (sidebar
   * highlight + topbar title) even though they don't share this item's own path
   * prefix — e.g. Category rules lives at its own /category-rules route rather than
   * nested under /categories, but conceptually belongs under the same nav entry. */
  activeAlsoOn?: string[];
}

interface NavSeparator {
  separator: true;
}

interface NavAction {
  action: "logout";
  label: string;
  icon: IconName;
}

type NavEntry = NavItem | NavSeparator | NavAction;

interface NavGroup {
  label: string;
  items: NavEntry[];
}

function isNavSeparator(entry: NavEntry): entry is NavSeparator {
  return "separator" in entry;
}

function isNavAction(entry: NavEntry): entry is NavAction {
  return "action" in entry;
}

// Grouped by cadence of use (04-frontend.md "Grouped left nav") — future routes join
// their declared group: Transactions → Accounting, Categories → Setup (Phase 5). Never
// append to a flat list. Within Setup, `{ separator: true }` entries split the group
// into visual sub-sections (Companies/Clients/Bank accounts, then Expense
// categories/Bank profiles, then Settings/Log out) without repeating the "Setup"
// label. Log out is deliberately an ordinary flow item here, not pinned to the
// sidebar's bottom: .sidebar spans both app-shell grid rows, so on a data-heavy page
// (e.g. Banking with many transactions) that stretches .main-content past 100vh, the
// grid row — and so .sidebar itself — stretches to match, dragging a bottom-pinned
// button far below the fold. Plain in-flow placement has no such failure mode.
const NAV_GROUPS: NavGroup[] = [
  {
    label: "Work",
    items: [
      { path: "/", label: "Dashboard", icon: "dashboard" },
      { path: "/timesheets", label: "Timesheets", icon: "timesheets" },
      { path: "/invoices", label: "Invoices", icon: "invoices" },
    ],
  },
  {
    label: "Accounting",
    items: [
      { path: "/banking", label: "Banking", icon: "banking" },
      { path: "/vat", label: "VAT", icon: "vat" },
      { path: "/loans", label: "Loans", icon: "loans" },
      { path: "/reports", label: "Reports", icon: "reports" },
      { path: "/year-end", label: "Year-end close", icon: "yearEnd" },
    ],
  },
  {
    label: "Setup",
    items: [
      { path: "/companies", label: "Companies", icon: "companies" },
      { path: "/clients", label: "Clients", icon: "clients" },
      { path: "/bank-accounts", label: "Bank Accounts", icon: "bankAccounts" },
      { separator: true },
      {
        path: "/categories",
        label: "Expense categories",
        icon: "categories",
        activeAlsoOn: ["/category-rules"],
      },
      { path: "/bank-profiles", label: "Bank profiles", icon: "bankProfiles" },
      { separator: true },
      { path: "/settings", label: "Settings", icon: "settings" },
      { separator: true },
      { action: "logout", label: "Log out", icon: "logout" },
    ],
  },
];

const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items).filter(
  (entry): entry is NavItem => !isNavSeparator(entry) && !isNavAction(entry),
);

/** "/" only matches exactly; every other item also matches its own sub-paths, so
 * e.g. /companies/:id keeps "Companies" highlighted while drilled into a detail page. */
function isNavItemActive(item: NavItem, path: string): boolean {
  if (item.path === "/") return path === "/";
  if (path === item.path || path.startsWith(`${item.path}/`)) return true;
  return (item.activeAlsoOn ?? []).some((p) => path === p || path.startsWith(`${p}/`));
}

function navLink(item: NavItem, onNavigate: () => void): Node {
  const fragment = html`<a href="${item.path}" data-link class="sidebar-link">${icon(item.icon)}<span>${item.label}</span></a>`;
  const link = fragment.firstElementChild as HTMLAnchorElement;
  bindClass(
    link,
    "active",
    computed(() => isNavItemActive(item, currentPath.value)),
  );
  link.addEventListener("click", onNavigate);
  return link;
}

/** No view previously exposed a way to end a session from the UI — found while
 * writing Phase 3b's two-stage-login retrofit spec, which needs a real logout to
 * re-trigger it (08-roadmap.md). `logout()` (services/auth.ts) already existed and
 * clears the session store; this just wires it to a visible control. A <button>
 * reusing .sidebar-link for identical layout/hover styling to the <a> nav items. */
function navLogoutButton(item: NavAction, onNavigate: () => void): Node {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "sidebar-link logout-btn";
  button.appendChild(icon(item.icon));
  const label = document.createElement("span");
  label.textContent = item.label;
  button.appendChild(label);
  button.addEventListener("click", () => {
    onNavigate();
    void logout().then(() => {
      location.assign("/login");
    });
  });
  return button;
}

/** html`` interpolates a single Node, not an array — collect the groups into one fragment first. */
function navList(groups: NavGroup[], onNavigate: () => void): DocumentFragment {
  const fragment = document.createDocumentFragment();
  for (const group of groups) {
    const groupEl = document.createElement("div");
    groupEl.className = "sidebar-group";
    const labelEl = document.createElement("div");
    labelEl.className = "sidebar-group-label";
    labelEl.textContent = group.label;
    groupEl.appendChild(labelEl);
    for (const item of group.items) {
      if (isNavSeparator(item)) {
        const divider = document.createElement("hr");
        divider.className = "sidebar-divider";
        groupEl.appendChild(divider);
      } else if (isNavAction(item)) {
        groupEl.appendChild(navLogoutButton(item, onNavigate));
      } else {
        groupEl.appendChild(navLink(item, onNavigate));
      }
    }
    fragment.appendChild(groupEl);
  }
  return fragment;
}

export interface AppShell {
  shell: Node;
  mainOutlet: Element;
}

/** Persistent chrome (sidebar + topbar) rendered once; the router only replaces mainOutlet's
 * children. Reactive bindings (nav highlighting, breadcrumb, mobile menu) need an active
 * scope — the shell lives for the app's whole lifetime, so this scope is deliberately never
 * disposed (there's no "leaving" the shell short of a full page navigation). */
export function buildShell(): AppShell {
  const { node: fragment } = effectScope(() => buildShellFragment());
  const mainOutlet = (fragment as DocumentFragment).querySelector(".main-content");
  if (!mainOutlet) throw new Error("shell markup missing .main-content");
  return { shell: fragment, mainOutlet };
}

function buildShellFragment(): Node {
  const mobileMenuOpen = signal(false);
  const closeMobileMenu = (): void => {
    mobileMenuOpen.value = false;
  };

  const activeNavLabel = computed(
    () => NAV_ITEMS.find((item) => isNavItemActive(item, currentPath.value))?.label ?? "",
  );
  // Deliberately keyed off the nav label rather than the breadcrumb: the breadcrumb
  // names the parent you'd go back to, which makes a worse tab title than the section
  // you are actually in.
  effect(() => {
    const label = activeNavLabel.value;
    document.title = label ? `${label} · Owlish Beans` : "Owlish Beans";
  });

  const hasBreadcrumb = computed(() => breadcrumb.value !== null);
  const breadcrumbLabel = computed(() => breadcrumb.value?.label ?? "");
  const breadcrumbPath = computed(() => breadcrumb.value?.path ?? "#");

  const fragment = html`<div class="app-shell">
    <nav class="sidebar">
      <a class="sidebar-brand" href="/" data-link>${brandLockup("head", "brand")}</a>
      <div class="sidebar-nav">${navList(NAV_GROUPS, closeMobileMenu)}</div>
      <div class="sidebar-footer">Powered by piofthings.ai</div>
    </nav>
    <header class="topbar">
      <button type="button" class="hamburger-btn" aria-label="Open menu" aria-pressed="false">
        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 6h18M3 12h18M3 18h18"></path></svg>
      </button>
      <div class="topbar-titles">
        <a class="topbar-breadcrumb" href="${breadcrumbPath}" data-link>‹ ${breadcrumbLabel}</a>
        <span class="topbar-title">${activeNavLabel}</span>
      </div>
      <div class="topbar-actions"></div>
    </header>
    <main class="main-content"></main>
  </div>`;

  const sidebar = fragment.querySelector<HTMLElement>(".sidebar");
  const hamburgerBtn = fragment.querySelector<HTMLButtonElement>(".hamburger-btn");
  const breadcrumbLink = fragment.querySelector<HTMLAnchorElement>(".topbar-breadcrumb");
  const titleEl = fragment.querySelector<HTMLElement>(".topbar-title");
  if (!sidebar || !hamburgerBtn || !breadcrumbLink || !titleEl) {
    throw new Error("shell markup missing expected elements");
  }

  bindClass(sidebar, "mobile-open", mobileMenuOpen);
  bindClass(hamburgerBtn, "active", mobileMenuOpen);
  hamburgerBtn.addEventListener("click", (event) => {
    event.stopPropagation();
    mobileMenuOpen.value = !mobileMenuOpen.value;
  });
  document.addEventListener("click", (event) => {
    if (!mobileMenuOpen.value) return;
    if (sidebar.contains(event.target as Node)) return;
    mobileMenuOpen.value = false;
  });

  bindShow(breadcrumbLink, hasBreadcrumb);
  bindShow(
    titleEl,
    computed(() => !hasBreadcrumb.value),
  );

  const topbarActions = fragment.querySelector(".topbar-actions");
  if (topbarActions) {
    populateCompanySwitcher(topbarActions);
  }

  return fragment;
}

/** Company switcher (04-frontend.md): plain DOM + a hard reload on change, matching
 * the hard-redirect pattern already used elsewhere for auth-state transitions —
 * simpler than threading a persistent, app-lifetime reactive scope through the shell. */
function populateCompanySwitcher(container: Element): void {
  const select = document.createElement("select");
  select.className = "company-switcher";
  container.appendChild(select);

  listCompanies()
    .then((companies) => {
      select.replaceChildren();
      for (const company of companies) {
        const option = document.createElement("option");
        option.value = company.id;
        option.textContent = company.name;
        select.appendChild(option);
      }

      const current = session.companyId.value;
      const firstCompany = companies[0];
      if (current && companies.some((c) => c.id === current)) {
        select.value = current;
      } else if (firstCompany) {
        session.setCompanyId(firstCompany.id);
        select.value = firstCompany.id;
      }
    })
    .catch(() => {
      // Company list failed to load; switcher stays empty — non-fatal for Phase 1.
    });

  select.addEventListener("change", () => {
    session.setCompanyId(select.value);
    location.reload();
  });
}
